import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import SearchModal from "./SearchModal";
import styles from "../ResourcesLayout.module.css";
import { DEMO_DESTINATION } from "../data/resourcesData";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGauge, faRightFromBracket } from "@fortawesome/free-solid-svg-icons";
import { useLogout } from "../../../api/login";
import { canAccessCms } from "../../../utils/permissions";

/**
 * ResourcesHeader — Minimal documentation header.
 *
 * - Left:   WebTwin logo + "Resources" label
 * - Center: Search trigger
 * - Right:  Home link + Explore Platform CTA
 */
const ResourcesHeader = ({ user }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);

  const navigate = useNavigate();
  const { mutateAsync: mutateAsyncLogout } = useLogout();

  const storedToken = localStorage.getItem("Token");
  const isAuthenticated = typeof storedToken !== "undefined" && Object.is(storedToken, null) === false && storedToken !== "null";

  const handleLogout = () => {
    const parsedToken = storedToken ? JSON.parse(storedToken) : null;
    if (parsedToken?.refresh_token) {
      mutateAsyncLogout({ refresh: parsedToken.refresh_token }).then(() => {
        localStorage.removeItem("Token");
        navigate("/login");
      });
    } else {
      localStorage.removeItem("Token");
      navigate("/login");
    }
  };

  // Keyboard shortcut: Ctrl/Cmd + K to open search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Close dropdown on click outside could be added, but for simplicity we'll just toggle it

  const isMac = typeof window !== "undefined" && navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  const shortcutLabel = isMac ? "⌘K" : "Ctrl K";

  return (
    <>
      <header className={styles["docs-header"]} id="resources-header">
        <div className={styles["docs-header-inner"]}>
          {/* Left — Brand */}
          <div className={styles["docs-brand"]}>
            <Link to="/" className={styles["docs-brand-name"]} style={{ textDecoration: "none" }}>
              WebTwin
            </Link>
            <span className={styles["docs-brand-separator"]} aria-hidden="true" />
            <Link to="/resources" className={styles["docs-brand-label"]} style={{ textDecoration: "none" }}>
              Resources
            </Link>
          </div>

          {/* Center — Search trigger */}
          <button
            className={styles["docs-search-trigger"]}
            onClick={() => setSearchOpen(true)}
            aria-label="Search documentation"
            id="resources-search-trigger"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <span className={styles["docs-search-text"]}>Search documentation...</span>
            <kbd className={styles["docs-search-kbd"]}>{shortcutLabel}</kbd>
          </button>

          {/* Right — Links */}
          <div className={styles["docs-header-right"]}>
            {isAuthenticated ? (
              <div className={`${styles["mobile-profile-container"]}`}>
                <button
                  className={styles["docs-mobile-profile-btn"]}
                  onClick={() => setAccountDropdownOpen(!accountDropdownOpen)}
                  aria-label="User account"
                  style={{ background: "#f3f4f6", border: "1px solid #d1d5db" }}
                >
                  <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#374151" }}>
                    {(user?.email?.[0] || "U").toUpperCase()}
                  </span>
                </button>
                
                {accountDropdownOpen && (
                  <div className={styles["account-dropdown"]}>
                    <span className={styles["account-email"]}>
                      {user?.email || "User Account"}
                    </span>
                    {canAccessCms(user) && (
                      <Link
                        to="/web-twinprocms"
                        target="_blank"
                        onClick={() => setAccountDropdownOpen(false)}
                        className={styles["account-dropdown-link"]}
                      >
                        <FontAwesomeIcon icon={faGauge} />
                        <span>TwinProcms Pro CMS</span>
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={handleLogout}
                      className={styles["account-dropdown-link"]}
                      style={{ color: "#dc2626" }}
                    >
                      <FontAwesomeIcon icon={faRightFromBracket} />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Desktop Auth */}
                <div className={`${styles["docs-header-auth"]} ${styles["desktop-only"]}`}>
                  <Link to="/login" className={styles["docs-header-login"]}>
                    Log in
                  </Link>
                  <button
                    type="button"
                    className={styles["docs-header-signup"]}
                    onClick={() => toast.info("Coming soon – stay tuned!")}
                  >
                    Sign up
                  </button>
                </div>

                {/* Mobile Profile Dropdown */}
                <div className={`${styles["mobile-profile-container"]} ${styles["mobile-only"]}`}>
                  <button
                    className={styles["docs-mobile-profile-btn"]}
                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                    aria-label="User profile"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </button>
                  
                  {profileDropdownOpen && (
                    <div className={styles["mobile-profile-dropdown"]}>
                      <Link to="/login" className={`${styles["docs-header-login"]} ${styles["mobile-dropdown-btn"]}`}>
                        Log in
                      </Link>
                      <button
                        type="button"
                        className={`${styles["docs-header-signup"]} ${styles["mobile-dropdown-btn"]}`}
                        onClick={() => {
                          toast.info("Coming soon – stay tuned!");
                          setProfileDropdownOpen(false);
                        }}
                      >
                        Sign up
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default ResourcesHeader;
